import { Box, Flex, Image, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import React, { forwardRef } from 'react';
import { maskDocument } from 'utils/formatters';
import { formatCurrency } from 'utils/number';
import { DateTime } from 'luxon';
import { Withdrawal, WithdrawSulcredi } from '..';
import { getVariable } from 'whitelabel';
import { maskAccountNumber } from 'utils/account';

const ComprovanteImpressao = ({ withdraw }: { withdraw: Withdrawal }, ref) => {
  const dataSulcredi = withdraw.withdrawSulcredi as WithdrawSulcredi | undefined;

  if (dataSulcredi) {
    return (
      <Box
        maxHeight="800px"
        minW="800px"
        maxW="800px"
        w={`100%`}
        ref={ref}
        px={3}
        backgroundColor={`#fff`}
      >
        <Flex
          flexDirection={`column`}
          w={`100%`}
          alignItems={`center`}
        >
          <VStack
            spacing={3}
            pt={`10px`}
            mb="5px"
          >
            <Image
              cursor="pointer"
              alt=""
              src={getVariable('logo')}
              maxWidth={64}
              maxHeight={20}
            />
          </VStack>
          <Text
            color={`brand.500`}
            fontSize={{ sm: `lg`, md: `xl`, lg: `lg` }}
            fontWeight="bold"
            alignItems={`center`}
          >
            Comprovante Transferência
          </Text>
          {dataSulcredi?.updatedAt && (
            <Text
              fontWeight={700}
              fontSize={12}
              mt={3}
              color={`#666`}
            >
              {DateTime.fromISO(dataSulcredi.updatedAt).toFormat('dd/MM/yyyy HH:mm:ss')}
            </Text>
          )}
        </Flex>
        <SimpleGrid
          w={`100%`}
          my={10}
          py={10}
          columns={1}
          spacing={4}
        >
          <SimpleGrid
            columns={2}
            spacing={2}
          >
            <Box>
              <Text
                fontSize={10}
                color={`#999`}
              >
                Status
              </Text>
              <Text
                color={`#333`}
                fontWeight={700}
                fontSize={14}
              >
                {dataSulcredi?.status === 2 ? 'Pago' : withdraw.status === 'IN_PAYMENT' ? 'Em Pagamento' : 'Pendente'}
              </Text>
            </Box>

            <Box>
              <Text
                fontSize={10}
                color={`#999`}
              >
                Tipo
              </Text>
              <Text
                color={`#333`}
                fontWeight={700}
                fontSize={14}
              >
                {`Débito`}
              </Text>
            </Box>
            <Box>
              <Text
                fontSize={10}
                color={`#999`}
              >
                Tipo
              </Text>
              <Text
                color={`#333`}
                fontWeight={700}
                fontSize={14}
              >
                {withdraw.type === 'BILLET' ? 'BOLETO' : 'PIX'}
              </Text>
            </Box>
            {dataSulcredi?.pixKey && (
              <Box>
                <Text
                  fontSize={10}
                  color={`#999`}
                >
                  Chave Pix
                </Text>
                <Text
                  color={`#333`}
                  fontWeight={700}
                  fontSize={14}
                >
                  {dataSulcredi.pixKey}
                </Text>
              </Box>
            )}

            <Box>
              <Text
                fontSize={10}
                color={`#999`}
              >
                Valor
              </Text>
              <Text
                color={`#333`}
                fontWeight={700}
                fontSize={14}
              >
                {formatCurrency(Math.abs(Number(withdraw?.amount)))}
              </Text>
            </Box>

            {dataSulcredi?.updatedAt && (
              <Box>
                <Text
                  fontSize={10}
                  color={`#999`}
                >
                  Liquidação
                </Text>
                <Text
                  color={`#333`}
                  fontWeight={700}
                  fontSize={14}
                >
                  {DateTime.fromISO(dataSulcredi?.updatedAt).toFormat('dd/MM/yyyy HH:mm:ss')}
                </Text>
              </Box>
            )}

            <Box>
              <Text
                fontSize={10}
                color={`#999`}
              >
                Descrição
              </Text>
              <Text
                color={`#333`}
                fontWeight={700}
              >
                {withdraw.description || `---`}
              </Text>
            </Box>

            <Box>
              <Text
                fontSize={10}
                color={`#999`}
              >
                Autenticação
              </Text>
              <Text
                color={`#333`}
                fontWeight={700}
                fontSize={14}
              >
                {dataSulcredi?.endToEndId}
              </Text>
            </Box>
          </SimpleGrid>

          <SimpleGrid
            columns={2}
            spacing={10}
          >
            {dataSulcredi?.debtorAccount && (
              <>
                <Text
                  mt={2}
                  color={`#ccc`}
                  fontWeight={700}
                  fontSize={15}
                >
                  QUEM PAGOU
                </Text>

                <Box>
                  <Text
                    fontSize={10}
                    color={`#999`}
                  >
                    Nome
                  </Text>
                  <Text
                    color={`#333`}
                    fontWeight={700}
                    fontSize={14}
                  >
                    {dataSulcredi?.debtorAccount?.name || '---'}
                  </Text>
                </Box>

                {dataSulcredi?.debtorAccount?.document && (
                  <Box>
                    <Text
                      fontSize={10}
                      color={`#999`}
                    >
                      CPF / CNPJ
                    </Text>
                    <Text
                      color={`#333`}
                      fontWeight={700}
                      fontSize={14}
                    >
                      {maskDocument(dataSulcredi.debtorAccount.document)}
                    </Text>
                  </Box>
                )}

                {(dataSulcredi?.debtorAccount?.ispb || dataSulcredi?.debtorAccount?.ispbName) && (
                  <Box>
                    <Text
                      fontSize={10}
                      color={`#999`}
                    >
                      Instituição
                    </Text>
                    <Text
                      color={`#333`}
                      fontWeight={700}
                      fontSize={14}
                    >
                      {[dataSulcredi?.debtorAccount?.ispb, dataSulcredi?.debtorAccount?.ispbName]
                        .filter(Boolean)
                        .join(' - ') || '---'}
                    </Text>
                  </Box>
                )}

                {dataSulcredi?.debtorAccount?.branch && (
                  <Box>
                    <Text
                      fontSize={10}
                      color={`#999`}
                    >
                      Agência
                    </Text>
                    <Text
                      color={`#333`}
                      fontWeight={700}
                      fontSize={14}
                    >
                      {dataSulcredi.debtorAccount.branch}
                    </Text>
                  </Box>
                )}

                {dataSulcredi?.debtorAccount?.account && (
                  <Box>
                    <Text
                      fontSize={10}
                      color={`#999`}
                    >
                      Conta
                    </Text>
                    <Text
                      color={`#333`}
                      fontWeight={700}
                      fontSize={14}
                    >
                      {dataSulcredi.debtorAccount.account}
                    </Text>
                  </Box>
                )}
              </>
            )}

            <Box>
              {dataSulcredi?.creditorAccount && (
                <>
                  <Text
                    mt={2}
                    color={`#ccc`}
                    fontWeight={700}
                    fontSize={15}
                  >
                    QUEM RECEBEU
                  </Text>
                  <Box>
                    <Text
                      fontSize={10}
                      color={`#999`}
                    >
                      Nome
                    </Text>
                    <Text
                      color={`#333`}
                      fontWeight={700}
                      fontSize={14}
                    >
                      {dataSulcredi?.creditorAccount?.name || '---'}
                    </Text>
                  </Box>
                  <Box>
                    <Text
                      fontSize={10}
                      color={`#999`}
                    >
                      Instituição
                    </Text>
                    <Text
                      color={`#333`}
                      fontWeight={700}
                      fontSize={14}
                    >
                      {[dataSulcredi?.creditorAccount?.ispb, dataSulcredi?.creditorAccount?.ispbName]
                        .filter(Boolean)
                        .join(' - ') || '---'}
                    </Text>
                  </Box>
                  <Box>
                    <Text
                      fontSize={10}
                      color={`#999`}
                    >
                      CPF / CNPJ
                    </Text>
                    <Text
                      color={`#333`}
                      fontWeight={700}
                      fontSize={14}
                    >
                      {!dataSulcredi?.creditorAccount?.document
                        ? '---'
                        : dataSulcredi?.creditorAccount?.document?.includes('*')
                        ? dataSulcredi?.creditorAccount?.document
                        : maskDocument(dataSulcredi?.creditorAccount?.document)}
                    </Text>
                  </Box>
                </>
              )}
            </Box>
          </SimpleGrid>
        </SimpleGrid>
      </Box>
    );
  }

  if (withdraw.receiver) {
    return (
      <Box
        maxHeight="800px"
        minW="800px"
        maxW="800px"
        w={`100%`}
        ref={ref}
        px={3}
        backgroundColor={`#fff`}
      >
        <Flex
          flexDirection={`column`}
          w={`100%`}
          alignItems={`center`}
        >
          <VStack
            spacing={3}
            pt={`10px`}
            mb="5px"
          >
            <Image
              cursor="pointer"
              alt=""
              src={getVariable('logo')}
              maxWidth={64}
              maxHeight={20}
            />
          </VStack>
          <Text
            color={`brand.500`}
            fontSize={{ sm: `lg`, md: `xl`, lg: `lg` }}
            fontWeight="bold"
            alignItems={`center`}
          >
            Comprovante Transferência
          </Text>
          {withdraw.createdAt && (
            <Text
              fontWeight={700}
              fontSize={12}
              mt={3}
              color={`#666`}
            >
              {DateTime.fromISO(withdraw.createdAt).toFormat('dd/MM/yyyy HH:mm:ss')}
            </Text>
          )}
        </Flex>
        <SimpleGrid
          w={`100%`}
          my={10}
          py={10}
          columns={1}
          spacing={4}
        >
          <SimpleGrid
            columns={2}
            spacing={2}
          >
            <Box>
              <Text
                fontSize={10}
                color={`#999`}
              >
                Status
              </Text>
              <Text
                color={`#333`}
                fontWeight={700}
                fontSize={14}
              >
                {withdraw.status === 'FINISHED' ? 'Pago' : withdraw.status === 'IN_PAYMENT' ? 'Em Pagamento' : 'Pendente'}
              </Text>
            </Box>

            <Box>
              <Text
                fontSize={10}
                color={`#999`}
              >
                Tipo
              </Text>
              <Text
                color={`#333`}
                fontWeight={700}
                fontSize={14}
              >
                {`Débito`}
              </Text>
            </Box>
            <Box>
              <Text
                fontSize={10}
                color={`#999`}
              >
                Tipo
              </Text>
              <Text
                color={`#333`}
                fontWeight={700}
                fontSize={14}
              >
                Transferência Interna
              </Text>
            </Box>
            {withdraw.receiver?.accountNumber && (
              <Box>
                <Text
                  fontSize={10}
                  color={`#999`}
                >
                  Conta
                </Text>
                <Text
                  color={`#333`}
                  fontWeight={700}
                  fontSize={14}
                >
                  {withdraw.receiver?.accountNumber}
                </Text>
              </Box>
            )}

            <Box>
              <Text
                fontSize={10}
                color={`#999`}
              >
                Valor
              </Text>
              <Text
                color={`#333`}
                fontWeight={700}
                fontSize={14}
              >
                {formatCurrency(Math.abs(Number(withdraw?.amount)))}
              </Text>
            </Box>

            {withdraw.createdAt && (
              <Box>
                <Text
                  fontSize={10}
                  color={`#999`}
                >
                  Liquidação
                </Text>
                <Text
                  color={`#333`}
                  fontWeight={700}
                  fontSize={14}
                >
                  {DateTime.fromISO(withdraw.createdAt).toFormat('dd/MM/yyyy HH:mm:ss')}
                </Text>
              </Box>
            )}

            <Box>
              <Text
                fontSize={10}
                color={`#999`}
              >
                Descrição
              </Text>
              <Text
                color={`#333`}
                fontWeight={700}
              >
                {withdraw.description || `---`}
              </Text>
            </Box>

            <Box>
              <Text
                fontSize={10}
                color={`#999`}
              >
                Autenticação
              </Text>
              <Text
                color={`#333`}
                fontWeight={700}
                fontSize={14}
              >
                {withdraw.id}
              </Text>
            </Box>
          </SimpleGrid>

          <SimpleGrid
            columns={2}
            spacing={10}
          >
            <VStack
              spacing={2}
              alignItems="start"
              w="full"
            >
              {withdraw.Customer && (
                <>
                  <Text
                    mt={2}
                    color={`#ccc`}
                    fontWeight={700}
                    fontSize={15}
                  >
                    QUEM PAGOU
                  </Text>

                  <Box>
                    <Text
                      fontSize={10}
                      color={`#999`}
                    >
                      Nome
                    </Text>
                    <Text
                      color={`#333`}
                      fontWeight={700}
                      fontSize={14}
                    >
                      {withdraw.Customer?.name}
                    </Text>
                  </Box>

                  <Box>
                    <Text
                      fontSize={10}
                      color={`#999`}
                    >
                      CPF / CNPJ
                    </Text>
                    <Text
                      color={`#333`}
                      fontWeight={700}
                      fontSize={14}
                    >
                      {maskDocument(withdraw.Customer?.document_number)}
                    </Text>
                  </Box>

                  <Box>
                    <Text
                      fontSize={10}
                      color={`#999`}
                    >
                      Conta
                    </Text>
                    <Text
                      color={`#333`}
                      fontWeight={700}
                      fontSize={14}
                    >
                      {maskAccountNumber(withdraw.Customer?.accountNumber)}
                    </Text>
                  </Box>
                </>
              )}
            </VStack>{' '}
            <VStack
              spacing={2}
              alignItems="start"
              w="full"
            >
              {withdraw.receiver && (
                <>
                  <Text
                    mt={2}
                    color={`#ccc`}
                    fontWeight={700}
                    fontSize={15}
                  >
                    QUEM RECEBEU
                  </Text>
                  <Box>
                    <Text
                      fontSize={10}
                      color={`#999`}
                    >
                      Nome
                    </Text>
                    <Text
                      color={`#333`}
                      fontWeight={700}
                      fontSize={14}
                    >
                      {withdraw.receiver?.name}
                    </Text>
                  </Box>

                  <Box>
                    <Text
                      fontSize={10}
                      color={`#999`}
                    >
                      CPF / CNPJ
                    </Text>
                    <Text
                      color={`#333`}
                      fontWeight={700}
                      fontSize={14}
                    >
                      {maskDocument(withdraw.receiver?.document_number)}
                    </Text>
                  </Box>
                  <Box>
                    <Text
                      fontSize={10}
                      color={`#999`}
                    >
                      Conta
                    </Text>
                    <Text
                      color={`#333`}
                      fontWeight={700}
                      fontSize={14}
                    >
                      {maskAccountNumber(withdraw.receiver?.accountNumber)}
                    </Text>
                  </Box>
                </>
              )}
            </VStack>
          </SimpleGrid>
        </SimpleGrid>
      </Box>
    );
  }

  if (withdraw.status === 'IN_PAYMENT') {
    return (
      <Box
        maxHeight="800px"
        minW="800px"
        maxW="800px"
        w={`100%`}
        ref={ref}
        px={3}
        backgroundColor={`#fff`}
      >
        <Flex
          flexDirection={`column`}
          w={`100%`}
          alignItems={`center`}
        >
          <VStack
            spacing={3}
            pt={`10px`}
            mb="5px"
          >
            <Image
              cursor="pointer"
              alt=""
              src={getVariable('logo')}
              maxWidth={64}
              maxHeight={20}
            />
          </VStack>
          <Text
            color={`brand.500`}
            fontSize={{ sm: `lg`, md: `xl`, lg: `lg` }}
            fontWeight="bold"
            alignItems={`center`}
          >
            Comprovante de Agendamento
          </Text>
          {withdraw.createdAt && (
            <Text
              fontWeight={700}
              fontSize={12}
              mt={3}
              color={`#666`}
            >
              {DateTime.fromISO(withdraw.createdAt).toFormat('dd/MM/yyyy HH:mm:ss')}
            </Text>
          )}
        </Flex>
        <SimpleGrid
          w={`100%`}
          my={10}
          py={10}
          columns={1}
          spacing={4}
        >
          <SimpleGrid
            columns={2}
            spacing={2}
          >
            <Box>
              <Text
                fontSize={10}
                color={`#999`}
              >
                Status
              </Text>
              <Text
                color={`#e5a100`}
                fontWeight={700}
                fontSize={14}
              >
                Em Pagamento
              </Text>
            </Box>

            <Box>
              <Text
                fontSize={10}
                color={`#999`}
              >
                Tipo
              </Text>
              <Text
                color={`#333`}
                fontWeight={700}
                fontSize={14}
              >
                {withdraw.type === 'BILLET' ? 'BOLETO' : withdraw.type === 'DICT' ? 'PIX' : withdraw.type === 'QRCODE' ? 'PIX (QR Code)' : withdraw.type}
              </Text>
            </Box>

            <Box>
              <Text
                fontSize={10}
                color={`#999`}
              >
                Valor
              </Text>
              <Text
                color={`#333`}
                fontWeight={700}
                fontSize={14}
              >
                {formatCurrency(Math.abs(Number(withdraw?.amount)))}
              </Text>
            </Box>

            <Box>
              <Text
                fontSize={10}
                color={`#999`}
              >
                Descrição
              </Text>
              <Text
                color={`#333`}
                fontWeight={700}
              >
                {withdraw.description || `---`}
              </Text>
            </Box>

            <Box>
              <Text
                fontSize={10}
                color={`#999`}
              >
                Autenticação
              </Text>
              <Text
                color={`#333`}
                fontWeight={700}
                fontSize={14}
              >
                {withdraw.endToEndId || withdraw.id}
              </Text>
            </Box>

            {withdraw.dueDate && (
              <Box>
                <Text
                  fontSize={10}
                  color={`#999`}
                >
                  Data de Vencimento
                </Text>
                <Text
                  color={`#333`}
                  fontWeight={700}
                  fontSize={14}
                >
                  {DateTime.fromISO(withdraw.dueDate).toFormat('dd/MM/yyyy')}
                </Text>
              </Box>
            )}
          </SimpleGrid>

          <SimpleGrid
            columns={2}
            spacing={10}
          >
            {withdraw.Customer && (
              <VStack
                spacing={2}
                alignItems="start"
                w="full"
              >
                <Text
                  mt={2}
                  color={`#ccc`}
                  fontWeight={700}
                  fontSize={15}
                >
                  QUEM PAGOU
                </Text>
                <Box>
                  <Text
                    fontSize={10}
                    color={`#999`}
                  >
                    Nome
                  </Text>
                  <Text
                    color={`#333`}
                    fontWeight={700}
                    fontSize={14}
                  >
                    {withdraw.Customer?.name}
                  </Text>
                </Box>
                <Box>
                  <Text
                    fontSize={10}
                    color={`#999`}
                  >
                    CPF / CNPJ
                  </Text>
                  <Text
                    color={`#333`}
                    fontWeight={700}
                    fontSize={14}
                  >
                    {maskDocument(withdraw.Customer?.document_number)}
                  </Text>
                </Box>
                {withdraw.Customer?.accountNumber && (
                  <Box>
                    <Text
                      fontSize={10}
                      color={`#999`}
                    >
                      Conta
                    </Text>
                    <Text
                      color={`#333`}
                      fontWeight={700}
                      fontSize={14}
                    >
                      {maskAccountNumber(withdraw.Customer?.accountNumber)}
                    </Text>
                  </Box>
                )}
              </VStack>
            )}

            {withdraw.CustomerAccounts && (
              <VStack
                spacing={2}
                alignItems="start"
                w="full"
              >
                <Text
                  mt={2}
                  color={`#ccc`}
                  fontWeight={700}
                  fontSize={15}
                >
                  DESTINATÁRIO
                </Text>
                {withdraw.CustomerAccounts?.accountHolder && (
                  <Box>
                    <Text
                      fontSize={10}
                      color={`#999`}
                    >
                      Nome
                    </Text>
                    <Text
                      color={`#333`}
                      fontWeight={700}
                      fontSize={14}
                    >
                      {withdraw.CustomerAccounts.accountHolder}
                    </Text>
                  </Box>
                )}
                {withdraw.CustomerAccounts?.accountHolderDocument && (
                  <Box>
                    <Text
                      fontSize={10}
                      color={`#999`}
                    >
                      CPF / CNPJ
                    </Text>
                    <Text
                      color={`#333`}
                      fontWeight={700}
                      fontSize={14}
                    >
                      {maskDocument(withdraw.CustomerAccounts.accountHolderDocument)}
                    </Text>
                  </Box>
                )}
                {withdraw.CustomerAccounts?.bankIspb && (
                  <Box>
                    <Text
                      fontSize={10}
                      color={`#999`}
                    >
                      Banco
                    </Text>
                    <Text
                      color={`#333`}
                      fontWeight={700}
                      fontSize={14}
                    >
                      {withdraw.CustomerAccounts.bankIspb}
                    </Text>
                  </Box>
                )}
                {withdraw.CustomerAccounts?.agency && (
                  <Box>
                    <Text
                      fontSize={10}
                      color={`#999`}
                    >
                      Agência
                    </Text>
                    <Text
                      color={`#333`}
                      fontWeight={700}
                      fontSize={14}
                    >
                      {withdraw.CustomerAccounts.agency}
                    </Text>
                  </Box>
                )}
                {withdraw.CustomerAccounts?.account && (
                  <Box>
                    <Text
                      fontSize={10}
                      color={`#999`}
                    >
                      Conta
                    </Text>
                    <Text
                      color={`#333`}
                      fontWeight={700}
                      fontSize={14}
                    >
                      {withdraw.CustomerAccounts.account}
                    </Text>
                  </Box>
                )}
                {withdraw.CustomerAccounts?.pix && (
                  <Box>
                    <Text
                      fontSize={10}
                      color={`#999`}
                    >
                      Chave Pix
                    </Text>
                    <Text
                      color={`#333`}
                      fontWeight={700}
                      fontSize={14}
                    >
                      {withdraw.CustomerAccounts.pix}
                    </Text>
                  </Box>
                )}
              </VStack>
            )}
          </SimpleGrid>

          <Box
            mt={4}
            p={3}
            borderRadius="md"
            bg="#fff8e6"
          >
            <Text
              color={`#e5a100`}
              fontWeight={700}
              fontSize={12}
              textAlign="center"
            >
              Este pagamento será processado em até 24 horas.
            </Text>
          </Box>
        </SimpleGrid>
      </Box>
    );
  }

  return (
    <Box ref={ref} px={3} backgroundColor={`#fff`}>
      <Text>Não há dados para exibir</Text>
    </Box>
  );
};

export default forwardRef(ComprovanteImpressao);
